import { Spacing } from '@persona-id/design-tokens';
import Color from 'color';
import { fieldStyles, LooseFormControlProps } from 'shared/styles/form';
import { FONT_SIZES, FONT_WEIGHTS, LINE_HEIGHTS } from 'shared/styles/variables';
import { css } from 'styled-components';

import { paragraphMixins } from 'design-system/mixins/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import {
  boxCss,
  colorCss,
  fontWeightCss,
  FormControlStates,
  sizeCss,
  State,
  Style,
  StyleKey,
  xyCss,
} from 'lib/inquiryStyles';
import ThemeSerialized from 'lib/interfaces/ThemeSerialized';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

export const containerStyles = css`
  margin-bottom: ${Spacing[4]};
  ${HolderClient.isDashboardFLE &&
  css`
    pointer-events: visible;
  `}
`;

type ContainerSSTStyles = {
  margin: Style<StyleKey.Margin>;
};

export const containerSSTStyles = (styles?: ContainerSSTStyles) => css`
  ${containerStyles}

  // default margin for SST - 0 0 16px 0
  margin: ${boxCss(styles?.margin?.base?.base)};
`;

export const materialLabelContainerStyles = css`
  margin-bottom: ${Spacing[2]};

  /* NOTE this is needed for material inputs */
  padding-top: ${Spacing[2]};
`;

export const materialContainerStyles = css`
  margin-bottom: ${Spacing[2]};

  /* NOTE this is needed for material inputs */
  position: relative;
  padding-top: ${Spacing[4]};
`;

export type ErrorSSTStyles = {
  'font-family': Style<StyleKey.FontFamily, 'error'>;
  'font-size': Style<StyleKey.FontSize, 'error'>;
  'font-weight': Style<StyleKey.FontWeight, 'error'>;
  'text-color': Style<StyleKey.TextColor, 'error'>;
};

export const errorStyles = css`
  color: ${({ theme }) => theme.colorError};
  font-size: ${({ theme }) => theme.fontSizeSmall};
  margin-top: ${Spacing[2]};
`;

export const errorSSTStyles = (styles?: ErrorSSTStyles) => css`
  ${errorStyles}

  color: ${colorCss(styles?.['text-color']?.error?.base)};
  font-family: ${fontFamilyCss(styles?.['font-family']?.error?.base)};
  font-size: ${sizeCss(styles?.['font-size']?.error?.base)};
  font-weight: ${fontWeightCss(styles?.['font-weight']?.error?.base)};
`;

export type LabelSSTStyles = {
  'font-family': Style<StyleKey.FontFamily, 'label'>;
  'font-size': Style<StyleKey.FontSize, 'label'>;
  'font-weight': Style<StyleKey.FontWeight, 'label'>;
  'line-height': Style<StyleKey.LineHeight, 'label'>;
  'letter-spacing': Style<StyleKey.LetterSpacing, 'label'>;
  'text-color': Style<StyleKey.TextColor, 'label', State.Base | State.Focused>;
};

export const labelStyles = css`
  label,
  legend {
    ${paragraphMixins.sm};
    font-weight: 500; /* sm text doesn't support bold */
    font-family: inherit; /* override mixin default of Rubik */
    margin-bottom: ${Spacing[2]};
    transition-duration: ${TransitionDuration.Fast};
  }

  &:focus-within label {
    color: ${({ theme }) => theme.colorPrimary};
  }
`;

export const labelSSTStyles = (styles?: LabelSSTStyles) => css`
  ${labelStyles}

  label, legend {
    color: ${colorCss(styles?.['text-color']?.label?.base)};
    font-family: ${fontFamilyCss(styles?.['font-family']?.label?.base)};
    font-size: ${sizeCss(styles?.['font-size']?.label?.base)};
    font-weight: ${fontWeightCss(styles?.['font-weight']?.label?.base)};
  }

  &:focus-within label {
    color: ${colorCss(styles?.['text-color']?.label?.focused)};
  }
`;

export type PlaceholderSSTStyles = {
  'font-family': Style<StyleKey.FontFamily, 'placeholder'>;
  'font-size': Style<StyleKey.FontSize, 'placeholder'>;
  'font-weight': Style<StyleKey.FontWeight, 'placeholder'>;
  'letter-spacing': Style<StyleKey.LetterSpacing, 'placeholder'>;
  'line-height': Style<StyleKey.LineHeight, 'placeholder'>;
  'text-color': Style<
    StyleKey.TextColor,
    'placeholder',
    State.Base | State.Focused | State.Disabled | State.Errored
  >;
};

// Opacity for placeholder is set on the backend, so they are all assigned as 1
export const placeholderSSTStyles = (styles?: PlaceholderSSTStyles) => ({
  BASE: css`
    &::placeholder {
      color: ${colorCss(styles?.['text-color']?.placeholder?.base)};
      font-family: ${fontFamilyCss(styles?.['font-family']?.placeholder?.base)};
      font-size: ${sizeCss(styles?.['font-size']?.placeholder?.base)};
      font-weight: ${fontWeightCss(styles?.['font-weight']?.placeholder?.base)};
      line-height: ${sizeCss(styles?.['line-height']?.placeholder?.base)};
      letter-spacing: ${sizeCss(styles?.['letter-spacing']?.placeholder?.base)};

      opacity: 1;
    }
  `,
  ERRORED: css`
    &::placeholder {
      color: ${colorCss(styles?.['text-color']?.placeholder?.errored)};
      opacity: 1;
    }
  `,
  FOCUSED: css`
    &::placeholder {
      color: ${colorCss(styles?.['text-color']?.placeholder?.focused)};
      opacity: 1;
    }
  `,
  DISABLED: css`
    &::placeholder {
      color: ${colorCss(styles?.['text-color']?.placeholder?.disabled)};
      // need to override -webkit-fill-color above
      -webkit-text-fill-color: ${colorCss(styles?.['text-color']?.placeholder?.disabled)};
      opacity: 1;
    }
  `,
});

export type InputFieldSSTStyles = {
  'font-family': Style<StyleKey.FontFamily, 'base'>;
  'font-size': Style<StyleKey.FontSize, 'value'>;
  'font-weight': Style<StyleKey.FontWeight, 'value'>;
  'letter-spacing': Style<StyleKey.LetterSpacing, 'base'>;
  'line-height': Style<StyleKey.LineHeight, 'value'>;
  'text-color': Style<StyleKey.TextColor, 'value', FormControlStates>;
  'background-color': Style<StyleKey.BackgroundColor, 'base', FormControlStates>;
  'border-color': Style<StyleKey.BorderColor, 'base', FormControlStates>;
  'border-radius': Style<StyleKey.BorderRadius>;
  'border-width': Style<StyleKey.BorderWidth>;
  'shadow-color': Style<StyleKey.ShadowColor>;
  'shadow-offset': Style<StyleKey.ShadowOffset>;
  'shadow-radius': Style<StyleKey.ShadowRadius>;
};

export type InputComponentTextStyles = InputFieldSSTStyles & PlaceholderSSTStyles & ErrorSSTStyles;

export const inputFieldAndPlaceholderSSTStyles = ({
  styles,
  error = false,
  themeErrorColor,
}: {
  styles?: InputComponentTextStyles;
  error?: boolean;
  themeErrorColor?: string;
}) => css`
  outline: 0 !important;
  transition: ${TransitionDuration.Fast};
  box-shadow: 0 0 0 0;

  font-family: ${fontFamilyCss(styles?.['font-family']?.base?.base)};
  font-size: ${sizeCss(styles?.['font-size']?.value?.base)};
  font-weight: ${fontWeightCss(styles?.['font-weight']?.value?.base)};
  line-height: ${sizeCss(styles?.['line-height']?.value?.base)};
  letter-spacing: ${sizeCss(styles?.['letter-spacing']?.base?.base)};

  border-radius: ${sizeCss(styles?.['border-radius']?.base?.base)};
  border-width: ${boxCss(styles?.['border-width']?.base?.base)};

  color: ${colorCss(styles?.['text-color']?.value?.base)};
  background-color: ${colorCss(styles?.['background-color']?.base?.base)};
  border-color: ${colorCss(styles?.['border-color']?.base?.base)};

  box-shadow: ${xyCss(styles?.['shadow-offset']?.base?.base)}
    ${sizeCss(styles?.['shadow-radius']?.base?.base)} 0
    ${colorCss(styles?.['shadow-color']?.base?.base)};

  ${placeholderSSTStyles(styles).BASE};

  ${error &&
  css`
    color: ${colorCss(styles?.['text-color']?.value?.errored)};
    background-color: ${colorCss(styles?.['background-color']?.base.errored) ??
    Color(themeErrorColor).fade(0.97).rgb().string()};
    border-color: ${colorCss(styles?.['border-color']?.base?.errored)};

    ${placeholderSSTStyles(styles).ERRORED};
  `}

  &:focus-within {
    color: ${colorCss(styles?.['text-color']?.value?.focused)};
    background-color: ${colorCss(styles?.['background-color']?.base?.focused)};
    border-color: ${colorCss(styles?.['border-color']?.base?.focused)};
    /* Use box-shadow to ensure that inner content doesn't collapse (e.g. from using border-width. */
    ${colorCss(styles?.['border-color']?.base?.focused) &&
    css`
      box-shadow: 0 0 0 1px ${colorCss(styles?.['border-color']?.base?.focused)};
    `}

    ${placeholderSSTStyles(styles).FOCUSED};
  }

  &:disabled {
    color: ${colorCss(styles?.['text-color']?.value?.disabled)};
    // fix disabled text being white on Safari
    // this happens on screens after government ID step
    -webkit-text-fill-color: ${colorCss(styles?.['text-color']?.value?.disabled)};
    background-color: ${colorCss(styles?.['background-color']?.base?.disabled)};
    border-color: ${colorCss(styles?.['border-color']?.base?.disabled)};

    ${placeholderSSTStyles(styles).DISABLED};
  }
`;

export const fieldSSTStyles = (
  formControls: LooseFormControlProps,
  theme: Partial<ThemeSerialized>,
  styles?: InputComponentTextStyles,
) => css`
  ${fieldStyles(formControls, theme)}

  ${styles != null &&
  inputFieldAndPlaceholderSSTStyles({
    styles,
    error: formControls.hasError,
    themeErrorColor: theme.colorError,
  })}
`;

/**
 * NOTE: Material labels are both labels and placeholders.
 * When the input isn't focused, the label acts as a placeholder, and we use placeholder colors.
 * When the input is focused, the label acts as a label, and we use label colors.
 * Error colors are also kind of random. This is messy.
 */
export const materialLabelStyles = css<{
  styles?: InputComponentTextStyles & LabelSSTStyles;
  focused?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
}>`
  font-family: ${({ styles, theme }) =>
    fontFamilyCss(styles?.['font-family']?.placeholder?.base) ?? theme.fontFamily};
  font-size: ${({ styles }) =>
    sizeCss(styles?.['font-size']?.placeholder?.base) ?? FONT_SIZES.input};
  font-weight: ${({ styles }) =>
    fontWeightCss(styles?.['font-weight']?.placeholder?.base) ?? FONT_WEIGHTS.regular};
  line-height: ${({ styles }) =>
    sizeCss(styles?.['line-height']?.placeholder?.base) ?? LINE_HEIGHTS.input};
  letter-spacing: ${({ styles }) => sizeCss(styles?.['letter-spacing']?.placeholder?.base)};
  position: absolute;
  pointer-events: none;
  left: ${Spacing[1]};
  top: ${Spacing[2]};
  transition: ${TransitionDuration.Fast} ease all;

  ${({ styles, theme, hasError, hasWarning, focused }) => {
    let focusColor: string | undefined;

    if (hasError) {
      // NOTE: no label errored style
      focusColor = colorCss(styles?.['text-color']?.error?.base) ?? theme.colorError;
    } else if (hasWarning) {
      focusColor = theme.colorWarning;
    } else {
      // NOTE: the only time the label uses label SST styles is when focused.
      // When not focused, it fully acts as a placeholder.
      focusColor = colorCss(styles?.['text-color']?.label?.focused) ?? theme.colorPrimary;
    }

    // We don't use the isFocused helper because the DOM structure of MaterialInput doesn't work well with it.
    if (focused) {
      return css`
        color: ${focusColor};
      `;
    }

    return css`
      color: ${colorCss(styles?.['border-color']?.base?.base) ?? theme.colorInputBorder};
      *:focus ~ & {
        color: ${focusColor};
      }
    `;
  }}

  &&& label {
    margin-bottom: 0;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
  }
`;

export const materialFieldSSTStyles = css<{
  styles?: InputComponentTextStyles;
  focused?: boolean;
  hasError?: boolean;
  hasWarning?: boolean;
}>`
  font-family: ${({ styles, theme }) =>
    fontFamilyCss(styles?.['font-family']?.base?.base ?? theme.fontFamily)};
  font-size: ${({ styles }) => sizeCss(styles?.['font-size']?.value?.base) ?? FONT_SIZES.input};
  font-weight: ${({ styles }) => styles?.['font-weight']?.value?.base ?? FONT_WEIGHTS.regular};
  line-height: ${({ styles }) =>
    sizeCss(styles?.['line-height']?.value?.base) ?? LINE_HEIGHTS.input};
  padding: ${Spacing[2]} ${Spacing[2]} ${Spacing[2]} ${Spacing[1]};
  width: 100%;
  display: block;
  border: none;
  border-bottom: 1px solid;
  border-radius: 0; /* Material inputs don't have borders per se, just an underline */
  background-color: ${({ styles, theme }) =>
    colorCss(styles?.['background-color']?.base?.base) ?? theme.colorInputBackground};
  color: ${({ styles, theme }) => colorCss(styles?.['text-color']?.value?.base) ?? theme.colorFont};

  transition: ${TransitionDuration.Fast} ease border-color;

  ${({ styles, theme, hasError, hasWarning, focused }) => {
    let focusColor: string | undefined;

    if (hasError) {
      focusColor = colorCss(styles?.['border-color']?.base?.errored) ?? theme.colorError;
    } else if (hasWarning) {
      focusColor = theme.colorWarning;
    } else {
      focusColor = colorCss(styles?.['border-color']?.base?.focused) ?? theme.colorPrimary;
    }

    // We don't use the isFocused helper because the DOM structure of MaterialInput doesn't work well with it.
    if (focused) {
      return css`
        border-color: ${focusColor};
      `;
    }

    return css`
      border-color: ${colorCss(styles?.['border-color']?.base?.base) ?? theme.colorInputBorder};
      *:focus {
        outline: none;
        border-color: ${focusColor};
      }
    `;
  }}
`;
